import React, { FC, ReactNode } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyFlowNode, JourneyFlowResult } from '../../../../models/journey-explorer/journey-explorer';
import { GoalInfo, formatNumber, formatSpan, flowWindowLabel, goalRatio } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';

const NOTABLE_SHARE = 0.05;

interface FlowStoryProps {
	flow: JourneyFlowResult;
	lookup: Map<string, InteractionInfo>;
	goal: GoalInfo | null;
}

const share = (part: number, whole: number): string => `${((part / whole) * 100).toFixed(0)}%`;

const FlowStory: FC<FlowStoryProps> = ({ flow, lookup, goal }) => {
	const anchor = flow.nodes.find((node) => node.kind === 'anchor');
	if (!anchor || anchor.customers === 0) {
		return null;
	}
	const label = (name: string | null) => resolveInfo(lookup, name ?? '').label;
	const anchorLabel = label(flow.anchor);
	const timed = flow.within !== 'any';
	const windowLabel = flowWindowLabel(flow.within);
	const next = flow.nodes.filter((node) => node.step === 1 && node.kind === 'interaction');
	const reached = <b>{formatNumber(anchor.customers)}</b>;
	const scored = goal && !goal.outcome && flow.anchor !== goal.name ? goal : null;
	const typically = (node: JourneyFlowNode) =>
		node.median_secs_from_anchor !== null ? (
			<>
				, typically within <b>{formatSpan(node.median_secs_from_anchor)}</b>
			</>
		) : null;

	const endNode = flow.nodes.find((node) => node.kind === 'end');
	const missed = flow.nodes.find((node) => node.kind === 'not_end');
	const endLabel = label(flow.end ?? null);
	const limit = (
		<>
			within <b>{flow.steps} interactions</b>
			{timed && (
				<>
					{' '}
					and <b>{windowLabel}</b>
				</>
			)}
		</>
	);

	let story: ReactNode;
	if (flow.direction === 'between') {
		story = endNode ? (
			<>
				Of the {reached} customers who reached {anchorLabel},{' '}
				<b>{share(endNode.customers, anchor.customers)}</b> went on to <b>{endLabel}</b> {limit}.
				{scored && flow.end !== scored.name && (
					<>
						{' '}
						<b className={styles.goal_text}>{endNode.reached_pct.toFixed(0)}%</b> of them reached{' '}
						{scored.label}
						{missed && <>, against {missed.reached_pct.toFixed(0)}% of those who did not</>}.
					</>
				)}
			</>
		) : (
			<>
				None of the {reached} customers who reached {anchorLabel} went on to <b>{endLabel}</b> {limit}.
			</>
		);
	} else if (!next.length) {
		if (!timed) {
			return null;
		}
		story = (
			<>
				Within <b>{windowLabel}</b> of {anchorLabel}, almost no customers do anything else. Widen the time
				window to see what follows.
			</>
		);
	} else if (!scored) {
		const [top] = next;
		story = (
			<>
				Of the {reached} customers who reached {anchorLabel}, <b>{share(top.customers, anchor.customers)}</b>{' '}
				{flow.direction === 'after' ? (
					<>
						went to <b>{label(top.name)}</b> next{typically(top)}
					</>
				) : (
					<>
						came straight from <b>{label(top.name)}</b>
					</>
				)}
				.
			</>
		);
	} else if (flow.direction === 'after') {
		const notable = next.filter((node) => node.customers >= anchor.customers * NOTABLE_SHARE);
		const focus = [...(notable.length ? notable : next)].sort((a, b) => a.reached_pct - b.reached_pct)[0];
		const ratio = goalRatio(focus.reached_pct, scored);
		story = (
			<>
				Of the {reached} customers who reached {anchorLabel}, <b>{share(focus.customers, anchor.customers)}</b>{' '}
				go to <b>{label(focus.name)}</b> next
				{timed ? (
					<>
						{' '}
						within <b>{windowLabel}</b>
					</>
				) : (
					typically(focus)
				)}
				, and {ratio < 1 ? 'only ' : ''}
				<b className={styles.goal_text}>{focus.reached_pct.toFixed(0)}% of them</b> go on to {scored.label}
				{ratio > 0 && <>, {ratio.toFixed(1)}× the average</>}.
			</>
		);
	} else {
		const [top] = next;
		story = (
			<>
				Of the {reached} customers who reached {anchorLabel}, <b>{share(top.customers, anchor.customers)}</b>{' '}
				came straight from <b>{label(top.name)}</b>, and{' '}
				<b className={styles.goal_text}>{top.reached_pct.toFixed(0)}%</b> of them went on to {scored.label}.
			</>
		);
	}

	return (
		<div className={styles.story}>
			<div className={styles.story_title}>THE STORY</div>
			{story}
		</div>
	);
};

export default FlowStory;
